import { createContext, createElement, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { User } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { firebaseAuth } from './firebase';
import { supabase } from './supabase';
import type { Category, MenuItem } from './data';
import type { Tier } from './access';

/* -------------------------------------------------------------------------
   Superadmin API client + admin session.

   All cross-tenant reads/writes go through the `superadmin` Supabase edge
   function (service-role, server-side). The browser only ever holds the
   admin's Firebase ID token; the edge fn verifies it + asserts
   platform_admins membership before running any action.

   This mirrors the merchant session pattern in lib/session.tsx, but the
   admin is a separate Firebase email/password user gated on `me`.
------------------------------------------------------------------------- */

/** Shape of a tenant row as returned to the Tenants UI (see SUPERADMIN_PLAN §3). */
export interface AdminTenant {
  id: string;
  businessName: string;
  slug: string;
  ownerName: string;
  email: string;
  phone: string | null;
  city: string;
  address: string | null;
  gstin: string | null;
  fssai: string | null;
  plan: Tier | null;
  status: 'active' | 'trial' | 'suspended' | 'churned';
  signupDate: string;
  lastActive: string | null;
  monthlyRevenue: number;
  ordersPerDay: number;
  outlets: number;
}

/** Returned by the `me` action. */
export interface AdminMe {
  uid: string;
  email: string;
  name: string | null;
  isSuperadmin: boolean;
}

/** Input shape for menu.upsertItem / menu.bulkImport. */
export type MenuItemInput = Partial<MenuItem> & { name: string };

/* ---- Action argument / return contracts (subset used by the UI) -------- */

export interface TenantsListArgs {
  search?: string;
  status?: string;
  plan?: string;
  city?: string;
  limit?: number;
  offset?: number;
}
export interface TenantsListResult {
  rows: AdminTenant[];
  total: number;
}

/* ---- Reporting panels (dashboard / analytics / finance / billing /
        entitlements / audit). All read-only. See SUPERADMIN_PLAN §2/§3. ---- */

/** `dashboard.stats` */
export interface DashboardStats {
  tenants: { total: number; active: number; trial: number; suspended: number; churned: number };
  mrr: number;
  newSignups30d: number;
  ordersToday: number;
  activity: AdminActivityRow[];
}

export interface AdminActivityRow {
  restaurantId?: string;
  type?: string;
  detail?: string;
  at?: string;
}

/** `analytics.summary` */
export interface AnalyticsSummary {
  revenueTrend: { month: string; revenue: number }[];
  planDistribution: { plan: Tier; count: number }[];
  topTenants: { restaurantId: string; name: string; revenue: number }[];
  signupTrend: { month: string; count: number }[];
}

/** `finance.revenue` */
export interface FinanceRevenue {
  mrr: number;
  arr: number;
  collected: number;
  outstanding: number;
  byMonth: { month: string; amount: number }[];
}

/** `billing.plans` */
export interface BillingPlansResult {
  plans: { tier: Tier; label: string; price: number; period: string }[];
}

/** `billing.subscriptions` / `tenants.subscription` */
export interface BillingSubscriptionRow {
  restaurantId: string;
  planTier?: Tier | null;
  status?: 'trialing' | 'active' | 'expired' | 'cancelled' | null;
  trialEndsAt?: string | null;
  currentPeriodEnd?: string | null;
}

/** Editable subscription fields (tenants.setSubscription). */
export interface SubscriptionPatch {
  status?: 'trialing' | 'active' | 'expired' | 'cancelled';
  planTier?: Tier | null;
  trialEndsAt?: string | null;
  currentPeriodEnd?: string | null;
}

/** One staff member of a tenant (tenants.staff). Never includes the PIN hash. */
export interface AdminStaffRow {
  id: string;
  name: string;
  role: string;
  phone: string | null;
  email: string | null;
  shift: string | null;
  status: string | null;
  active: boolean;
  hasPin: boolean;
  joinedDate: string | null;
  lastActive: string | null;
}
export interface BillingSubscriptionsResult {
  rows: BillingSubscriptionRow[];
}

/** `billing.payments` (may be empty if no payments table yet) */
export interface BillingPaymentsResult {
  rows: Record<string, unknown>[];
}

/** `entitlements.get` — editable feature matrix (DB-backed). */
export interface EntitlementsResult {
  plans: { tier: Tier; label: string; price: number; rank?: number }[];
  features: { feature: string; tier: Tier; minTier?: Tier; enabled?: boolean; label?: string | null }[];
}

/** A per-tenant feature override row. */
export interface TenantOverrideRow {
  restaurantId: string;
  feature: string;
  enabled: boolean;
}

/** `plans.list` / `plans.upsert` */
export interface PlanRow {
  tier: string;
  label: string;
  price: number;
  period: string;
  rank: number;
  active: boolean;
  /** Marketing feature bullets shown on pricing cards / paywall / POS. */
  features?: string[];
  /** Original ("list") price. When > price it's shown struck-through. */
  mrp?: number | null;
}

/** `coupons.list` */
export interface CouponRow {
  code: string;
  description?: string | null;
  discountType: 'percent' | 'flat';
  discountValue: number;
  appliesTo: 'subscription' | 'order';
  maxRedemptions?: number | null;
  redeemedCount?: number;
  minAmount?: number | null;
  expiresAt?: string | null;
  active: boolean;
  createdAt?: string;
  createdBy?: string | null;
}

/** `announcements.list` */
export interface AnnouncementRow {
  id?: string;
  title: string;
  body: string;
  level: 'info' | 'warning' | 'critical';
  audience: string;
  active: boolean;
  startsAt?: string | null;
  endsAt?: string | null;
  createdAt?: string;
  createdBy?: string | null;
}

/** `admins.list` */
export interface AdminRow {
  uid: string;
  email: string;
  name?: string | null;
  role: string;
  created_at?: string;
}

/** `audit.list` */
export interface AuditListResult {
  rows: AdminActivityRow[];
}

/**
 * Invoke a single `superadmin` edge-function action.
 *
 * Body shape: `{ action, ...args }`. The function returns either the action
 * payload directly or `{ error }` on failure; we throw on the latter (and on
 * transport errors) so callers can use try/catch.
 */
export async function callAdmin<T = unknown>(action: string, args: object = {}): Promise<T> {
  const { data, error } = await supabase.functions.invoke('superadmin', {
    body: { action, ...args },
  });
  if (error) {
    // FunctionsHttpError carries the JSON body in `context`; try to surface the
    // server's `{ error }` message, else fall back to the transport message.
    let msg = error.message;
    try {
      const body = await (error as { context?: { json?: () => Promise<unknown> } }).context?.json?.();
      if (body && typeof body === 'object' && 'error' in body) {
        msg = String((body as { error: unknown }).error);
      }
    } catch {
      /* ignore parse errors, keep transport message */
    }
    throw new Error(msg || `superadmin:${action} failed`);
  }
  if (data && typeof data === 'object' && 'error' in data) {
    throw new Error(String((data as { error: unknown }).error));
  }
  return data as T;
}

/**
 * Translate raw superadmin edge-fn / PostgREST errors into actionable
 * deploy-side hints. Returns the original message when no known cause matches.
 */
export function explainAdminError(msg: string): string {
  const m = msg.toLowerCase();
  if (m.includes('unknown_action')) {
    return (
      'The deployed "superadmin" edge function is older than this UI and is missing this action. ' +
      'Redeploy it from ezydineweb: `supabase functions deploy superadmin --no-verify-jwt`.'
    );
  }
  if (
    (m.includes('relation') && m.includes('does not exist')) ||
    m.includes('schema cache') ||
    m.includes('pgrst205') ||
    m.includes('42p01')
  ) {
    return (
      'A required table is missing in the database — the subscriptions setup ' +
      '(ezydine/supabase/subscriptions.sql) has probably not been applied yet. ' +
      `Original error: ${msg}`
    );
  }
  return msg;
}

/* ------------------------------- Session -------------------------------- */

interface AdminSessionCtx {
  user: User;
  me: AdminMe;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AdminSessionCtx | null>(null);

export function useAdmin(): AdminSessionCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAdmin must be used inside <AdminRequireAuth>');
  return v;
}

/**
 * Gates the `/superadmin` surface. On Firebase auth resolve:
 *  - no user            → redirect to /superadmin/login
 *  - user but not admin → sign out + redirect to /superadmin/login
 *  - valid admin        → render children with context
 */
export function AdminRequireAuth({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [me, setMe] = useState<AdminMe | null>(null);

  useEffect(() => {
    const unsub = firebaseAuth.onAuthStateChanged(async (u) => {
      if (!u) {
        navigate('/superadmin/login', { replace: true });
        return;
      }
      try {
        const result = await callAdmin<AdminMe>('me');
        if (!result?.isSuperadmin) throw new Error('Not a platform admin');
        setUser(u);
        setMe(result);
      } catch {
        await firebaseAuth.signOut();
        navigate('/superadmin/login', { replace: true });
      }
    });
    return unsub;
  }, [navigate]);

  if (!user || !me) {
    return createElement(
      'div',
      { className: 'min-h-screen flex items-center justify-center bg-background-100' },
      createElement('i', { className: 'ri-loader-4-line text-2xl text-primary-500 animate-spin' })
    );
  }

  const signOut = async () => {
    await firebaseAuth.signOut();
    navigate('/superadmin/login', { replace: true });
  };

  const value: AdminSessionCtx = { user, me, signOut };
  return createElement(Ctx.Provider, { value }, children);
}

/* --------------------------- Typed action helpers ----------------------- */

export const adminApi = {
  me: () => callAdmin<AdminMe>('me'),

  tenants: {
    list: (args: TenantsListArgs = {}) => callAdmin<TenantsListResult>('tenants.list', args),
    setStatus: (restaurantId: string, status: 'active' | 'suspended') =>
      callAdmin<{ ok: boolean }>('tenants.setStatus', { restaurantId, status }),
    setPlan: (restaurantId: string, tier: Tier, currentPeriodEnd?: string) =>
      callAdmin<{ ok: boolean }>('tenants.setPlan', { restaurantId, tier, currentPeriodEnd }),
    impersonateLink: (restaurantId: string) =>
      callAdmin<{ url: string }>('tenants.impersonateLink', { restaurantId }),

    // Edit business profile + owner contact
    update: (
      restaurantId: string,
      patch: {
        restaurant?: Partial<{ name: string; phone: string; address: string; gstin: string; fssai: string; email: string }>;
        owner?: Partial<{ name: string; email: string; phone: string }>;
      },
    ) => callAdmin<{ ok: boolean }>('tenants.update', { restaurantId, ...patch }),

    // Subscription editor
    subscription: (restaurantId: string) =>
      callAdmin<{ subscription: BillingSubscriptionRow | null }>('tenants.subscription', { restaurantId }),
    setSubscription: (restaurantId: string, patch: SubscriptionPatch) =>
      callAdmin<{ ok: boolean }>('tenants.setSubscription', { restaurantId, ...patch }),
    extendTrial: (restaurantId: string, days: number) =>
      callAdmin<{ ok: boolean; trialEndsAt: string }>('tenants.extendTrial', { restaurantId, days }),

    // Team (the tenant's staff)
    staff: (restaurantId: string) =>
      callAdmin<{ rows: AdminStaffRow[] }>('tenants.staff', { restaurantId }),
    setStaffActive: (restaurantId: string, staffId: string, active: boolean) =>
      callAdmin<{ ok: boolean }>('tenants.setStaffActive', { restaurantId, staffId, active }),
    deleteStaff: (restaurantId: string, staffId: string) =>
      callAdmin<{ ok: boolean }>('tenants.deleteStaff', { restaurantId, staffId }),
    resetStaffPin: (restaurantId: string, staffId: string, pin: string) =>
      callAdmin<{ ok: boolean }>('tenants.resetStaffPin', { restaurantId, staffId, pin }),
  },

  menu: {
    categories: (restaurantId: string) =>
      callAdmin<{ categories: Category[] }>('menu.categories', { restaurantId }),
    addCategory: (restaurantId: string, name: string, icon?: string) =>
      callAdmin<{ category: Category }>('menu.addCategory', { restaurantId, name, icon }),
    items: (restaurantId: string) =>
      callAdmin<{ items: MenuItem[] }>('menu.items', { restaurantId }),
    upsertItem: (restaurantId: string, item: MenuItemInput) =>
      callAdmin<{ item: MenuItem }>('menu.upsertItem', { restaurantId, item }),
    deleteItem: (restaurantId: string, id: string) =>
      callAdmin<{ ok: boolean }>('menu.deleteItem', { restaurantId, id }),
    bulkImport: (restaurantId: string, items: MenuItemInput[]) =>
      callAdmin<{ inserted: number }>('menu.bulkImport', { restaurantId, items }),
  },

  dashboard: {
    stats: () => callAdmin<DashboardStats>('dashboard.stats'),
  },

  analytics: {
    summary: (args: { from?: string; to?: string } = {}) =>
      callAdmin<AnalyticsSummary>('analytics.summary', args),
  },

  finance: {
    revenue: (args: { from?: string; to?: string } = {}) =>
      callAdmin<FinanceRevenue>('finance.revenue', args),
  },

  billing: {
    plans: () => callAdmin<BillingPlansResult>('billing.plans'),
    subscriptions: (args: { search?: string; status?: string } = {}) =>
      callAdmin<BillingSubscriptionsResult>('billing.subscriptions', args),
    payments: (args: { from?: string; to?: string; limit?: number } = {}) =>
      callAdmin<BillingPaymentsResult>('billing.payments', args),
  },

  entitlements: {
    get: () => callAdmin<EntitlementsResult>('entitlements.get'),
    setFeature: (args: { feature: string; minTier?: Tier; enabled?: boolean; label?: string }) =>
      callAdmin<{ ok: boolean }>('entitlements.setFeature', args),
    tenantOverrides: (restaurantId: string) =>
      callAdmin<{ rows: TenantOverrideRow[] }>('entitlements.tenantOverrides', { restaurantId }),
    setTenantOverride: (restaurantId: string, feature: string, enabled: boolean | null) =>
      callAdmin<{ ok: boolean }>('entitlements.setTenantOverride', { restaurantId, feature, enabled }),
  },

  plans: {
    list: () => callAdmin<{ plans: PlanRow[] }>('plans.list'),
    upsert: (plan: Partial<PlanRow> & { tier: string; label: string }) =>
      callAdmin<{ ok: boolean }>('plans.upsert', { plan }),
    delete: (tier: string) => callAdmin<{ ok: boolean }>('plans.delete', { tier }),
  },

  coupons: {
    list: () => callAdmin<{ rows: CouponRow[] }>('coupons.list'),
    upsert: (coupon: CouponRow) => callAdmin<{ ok: boolean }>('coupons.upsert', { coupon }),
    delete: (code: string) => callAdmin<{ ok: boolean }>('coupons.delete', { code }),
  },

  announcements: {
    list: () => callAdmin<{ rows: AnnouncementRow[] }>('announcements.list'),
    upsert: (announcement: AnnouncementRow) =>
      callAdmin<{ ok: boolean }>('announcements.upsert', { announcement }),
    delete: (id: string) => callAdmin<{ ok: boolean }>('announcements.delete', { id }),
  },

  admins: {
    list: () => callAdmin<{ rows: AdminRow[] }>('admins.list'),
    add: (args: { uid: string; email: string; name?: string; role?: string }) =>
      callAdmin<{ ok: boolean }>('admins.add', args),
    remove: (uid: string) => callAdmin<{ ok: boolean }>('admins.remove', { uid }),
  },

  system: {
    audit: (args: { limit?: number; restaurantId?: string; type?: string } = {}) =>
      callAdmin<AuditListResult>('audit.list', args),
  },
};
